<template>
    <common-content-spacer height="medium">
        <div class="practice-members-list__filters">
            <div class="practice-members-list__filters--list">
                <practice-members-list-filter :orderBy="orderBy" @filter-changed="filterChanged"></practice-members-list-filter>
            </div>
            <div class="practice-members-list__filters--invite">
                <invite-dropdown
                    :openInviteModal="openAreYouSureModal"
                    :menuItems="menuItemsInvite"
                    :buttonLabel="$t('elements.practice_member_filter.buttons.invite_practice_members')"
                ></invite-dropdown>
            </div>
        </div>
        <common-loader v-if="isLoading" :isProcessing="isLoading"></common-loader>
        <common-grid v-else>
            <common-grid-column v-for="member in practiceMembers" :key="member.id">
                <practice-member-card
                    :physician="member"
                    :showNetworkInclusionStatus="false"
                    :showMasterUserAuthorities="true"
                    @membership-status-change="membershipStatusChanged($event, member)"
                    @member-removed="removeMember($event)"
                    @privileges-updated="privilegesUpdated($event)"
                    @checkAvailability="checkAvailability($event)"
                    @invitation-resent="invitationResent()"
                ></practice-member-card>
            </common-grid-column>
            <div v-if="!practiceMembers.length" class="practice-members-list__empty-state">
                {{ $t('elements.referrals.card.no_members') }}
            </div>
        </common-grid>

        <common-buttons v-show="totalCount > practiceMembers.length">
            <common-button
                :label="$t('pages.physicians.show.buttons.view_more')"
                type="primary"
                @click="viewMore"
            ></common-button>
        </common-buttons>

        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>

        <send-join-invites-modal
            v-if="isInviteModalOpen"
            :headerText="$t('modals.join_invites_member.heading', { practiceName: $auth.getPractice().name })"
            :label="$t('modals.join_invites_member.label')"
            :placeholder="$t('modals.join_invites_member.placeholder')"
            :buttonLabel="$t('elements.home.connections.modals.invite.ancillary.button_label')"
            :practiceType="$auth.getPractice().practiceType"
            :inviteBy="data.inviteBy"
            :isInviteToPractice="true"
            @join-invites-sent="invitationsSent"
            @new-email-invitations-present="sendInvitesAfterEmailCheck"
            @close="closeInviteModal"
        ></send-join-invites-modal>
        <common-are-you-sure-modal
            v-if="isAreYouSureModalOpen"
            :headingText="$t('pages.referrals.show.disclaimer_modal.heading')"
            :contentText="$t('pages.practice.billing.invite_new_member_warning')"
            :noButtonText="$t('common.microcopy.close')"
            :yesButtonText="$t('common.microcopy.continue')"
            :showWarningIcon="true"
            @close="isAreYouSureModalOpen = false"
            @continue="openInviteModal"
        ></common-are-you-sure-modal>
    </common-content-spacer>
</template>

<script>
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import PracticeMembersListFilter from 'components/page-elements/practice/PracticeMembersListFilter'
    import PracticeMembersAPI from 'api/PracticeMembersAPI'
    import UserMembershipStatusEnum from '../../../enums/UserMembershipStatusEnum'
    import UserRolesEnum from '../../../enums/UserRolesEnum'
    import InviteDropdown from 'components/page-elements/invites/InviteDropdown'
    import UsersAPI from 'api/UsersAPI'
    import Store from '@/store'
    import InviteByEnum from '@/enums/InviteByEnum'
    import SendJoinInvitesModal from 'components/page-elements/invites/SendJoinInvitesModal'

    export default {
        components: {
            PracticeMemberCard,
            PracticeMembersListFilter,
            InviteDropdown,
            SendJoinInvitesModal,
        },

        data() {
            return {
                data: {
                    inviteBy: InviteByEnum.INVITE_BY_EMAIL,
                },
                practiceMembers: [],
                offset: 0,
                totalCount: 0,
                userRoleId: undefined,
                practiceMembershipStatus: null,
                orderBy: 'firstName asc,lastName asc',
                isInviteModalOpen: false,
                isEmailCheckModalOpen: false,
                joinInviteErrorMessageCode: null,
                notificationMessage: '',
                isDropdownOpen: false,
                isAreYouSureModalOpen: false,
                isLoading: false,
            }
        },

        computed: {
            limit() {
                return this.$route.query.limit
            },

            menuItemsInvite() {
                return [
                    {
                        name: this.$t('elements.practice_member_filter.buttons.invite_by_email'),
                        payload: { inviteBy: 'invite_by_email' },
                    },
                    {
                        name: this.$t('elements.practice_member_filter.buttons.invite_by_phone'),
                        payload: { inviteBy: 'invite_by_phone' },
                    },
                ]
            },
        },

        methods: {
            toggleDropdown() {
                this.isDropdownOpen = !this.isDropdownOpen
            },

            closeDropdown() {
                this.isDropdownOpen = false
            },

            fetchPracticeMembers({ limit, offset, practiceMembershipStatus, userRoleId, append = false }) {
                this.isLoading = true
                PracticeMembersAPI.getList(this.$auth.getPractice().id, {
                    limit,
                    offset,
                    practiceMembershipStatus,
                    userRoleId,
                    sort: this.orderBy,
                })
                    .then((res) => {
                        this.totalCount = parseInt(res._headers['x-total-count'])
                        if (append) {
                            return (this.practiceMembers = this.practiceMembers.concat(res))
                        }
                        this.practiceMembers = res
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            viewMore() {
                this.offset = this.offset + this.limit
                this.fetchPracticeMembers({
                    limit: this.limit,
                    offset: this.offset,
                    userRoleId: this.userRoleId,
                    practiceMembershipStatus: this.practiceMembershipStatus,
                    append: true,
                })
            },

            memberApproved(member) {
                member.practiceMembershipStatus = UserMembershipStatusEnum.APPROVED
            },

            removeMember(member, showNotification = true) {
                const index = this.practiceMembers.indexOf(member)
                if (index > -1) {
                    this.practiceMembers.splice(index, 1)
                }

                const offset = this.limit + this.offset - 1
                this.fetchPracticeMembers({
                    limit: 1,
                    offset,
                    userRoleId: this.userRoleId,
                    practiceMembershipStatus: this.practiceMembershipStatus,
                    append: true,
                })

                this.notificationMessage = this.$t('pages.practice.members.member_removed')

                if (showNotification) {
                    this.$refs.commonSuccessNotificationChild.showNotification()
                    this.removePracticeMemberFromStore(member)
                }
            },

            invitationResent() {
                this.notificationMessage = this.$t('pages.practice.members.invitation_resent')
                this.$refs.commonSuccessNotificationChild.showNotification()
            },

            removePracticeMemberFromStore(member) {
                const practiceMembers = this.$auth.getMembers()
                Store.dispatch(
                    'auth/setMembers',
                    practiceMembers.filter((m) => m.id !== member.id),
                )
            },

            membershipStatusChanged(action, member) {
                if (action === 'approve') return this.memberApproved(member)

                this.removeMember(member, false)
                this.removePracticeMemberFromStore(member)
            },

            filterChanged(event) {
                this.offset = 0
                if (event.filter === UserRolesEnum.PHYSICIAN.NAME) {
                    this.userRoleId = UserRolesEnum.PHYSICIAN.ID
                    this.practiceMembershipStatus = null
                } else if (event.filter === 'PendingApproval') {
                    this.practiceMembershipStatus = UserMembershipStatusEnum.PENDING
                    this.userRoleId = null
                } else {
                    this.practiceMembershipStatus = null
                    this.userRoleId = null
                }

                this.orderBy = event.orderBy

                this.fetchPracticeMembers({
                    limit: this.limit,
                    offset: this.offset,
                    userRoleId: this.userRoleId,
                    practiceMembershipStatus: this.practiceMembershipStatus,
                })
            },

            closeInviteModal() {
                this.isInviteModalOpen = false
            },

            openInviteModal() {
                this.isAreYouSureModalOpen = false
                this.isInviteModalOpen = true
            },

            openAreYouSureModal(payload) {
                this.data.inviteBy = payload.inviteBy
                this.isAreYouSureModalOpen = true
            },

            closeEmailCheckModal() {
                this.isEmailCheckModalOpen = false
            },

            openEmailCheckModal(event) {
                this.closeInviteModal()
                this.emailsCheck = event.checkProperty
                this.invitePracticeType = event.practiceType
                this.isEmailCheckModalOpen = true
            },

            sendInvitesAfterEmailCheck(event) {
                UsersAPI.createJoinPracticeInvites({
                    userId: this.$auth.getActiveMember().id,
                    emails: event.emails,
                    practiceId: this.$auth.getPractice().id,
                }).then(() => {})
            },

            invitationsSent() {
                this.notificationMessage = this.$t('modals.join_invites_member.invites_sent')
                this.closeInviteModal()
                this.$refs.commonSuccessNotificationChild.showNotification()
            },

            privilegesUpdated(data) {
                const member = this.practiceMembers.find((member) => member.id === data.userId)
                member.isMasterUser = data.canManagePractice
                member.canModifyPracticeMemberAvailability = data.canModifyPracticeMemberAvailability
            },
        },

        mounted() {
            if (this.$route.query?.openModal?.length) {
                this.openInviteModal({ inviteBy: this.$route.query?.openModal })
                const query = Object.assign({}, this.$route.query)
                delete query.openModal
                this.$router.replace({ query })
            }
        },

        created() {
            this.fetchPracticeMembers({ limit: this.limit, offset: this.offset })
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .practice-members-list {
        &__filter {
            width: 100%;
            display: flex;
            align-items: center;
        }
        &__button {
            min-width: 200px;
        }
        &__filters {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;

            &--list {
                @include breakpoint(tablet) {
                    width: 100%;
                    margin-bottom: 30px;
                }
                width: calc(80% - 220px);
            }

            &--invite {
                @include breakpoint(tablet) {
                    width: 100%;
                }
                width: 220px;
            }
        }

        &__empty-state {
            padding: 40px 20px;
        }
    }
</style>
